import React, { useState, useEffect, useRef } from 'react';
import { t } from '@lingui/core/macro';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './VideoSettings.css';
import { Select, MenuItem, SelectChangeEvent } from "@mui/material";

interface VideoSettingsProps {
  onSettingsChange: (settings: VideoSettings) => void | Promise<void>;
  initialSettings?: VideoSettings;
}

export interface VideoSettings {
  background_color: string;
  fps_num: number;
  video_width: number;
  transparent: boolean;
  video_time: number;
  aspect_ratio: string;
  video_format: string;
  fonts?: Record<string, string>;
  logos?: Record<string, { url: string; isEnabled: boolean }>;
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  background_color: '#000000',
  fps_num: 30,
  video_width: 1920,
  transparent: false,
  video_time: 0,
  aspect_ratio: '16:9',
  video_format: 'H264',
  fonts: {},
  logos: {},
};

// Calculate height dynamically from width and aspect ratio
function calculateVideoHeight(settings: VideoSettings): number {
  const width = settings.video_width ?? 1920;
  const aspectRatio = settings.aspect_ratio;

  if (typeof aspectRatio === 'string' && aspectRatio.includes(':')) {
    const [w, h] = aspectRatio.split(':').map(Number);
    if (w && h) {
      return Math.round(width * (h / w));
    }
  }
  return 1080; // fallback height
}

const VideoSettingsComponent: React.FC<VideoSettingsProps> = ({ onSettingsChange, initialSettings }) => {
  const [settings, setSettings] = useState<VideoSettings>({
    ...DEFAULT_VIDEO_SETTINGS,
    ...initialSettings,
  });

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [timeMode, setTimeMode] = useState<'total' | 'pixels'>('total');
  const [videoTimeInput, setVideoTimeInput] = useState(
    initialSettings?.video_time?.toString() || ""
  );
  const lastSentSettingsRef = useRef<VideoSettings | null>(null);

  useEffect(() => {
    const contentHeight = calculateVideoHeight(settings);
    let videoTimeToSend = settings.video_time;

    if (timeMode === 'pixels' && settings.video_time > 0) {
      videoTimeToSend = Number((contentHeight / settings.video_time).toFixed(2));
    }

    const currentSettingsToSend: VideoSettings = {
      ...settings,
      video_time: videoTimeToSend,
    };

    const last = lastSentSettingsRef.current;
    const keysToCompare: (keyof VideoSettings)[] = [
      'background_color',
      'fps_num',
      'video_width',
      'transparent',
      'aspect_ratio',
      'video_time',
      'video_format',
    ];

    const hasChanged =
      !last ||
      keysToCompare.some(
        key => currentSettingsToSend[key] !== last[key]
      );

    if (hasChanged) {
      lastSentSettingsRef.current = currentSettingsToSend;
      const result = onSettingsChange(currentSettingsToSend);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onSettingsChange:', error);
        });
      }
    }
  }, [settings, timeMode, onSettingsChange]);

  // Handlers
  const handleFpsChange = (e: SelectChangeEvent<string>) => {
    setSettings(prev => ({ ...prev, fps_num: parseInt(e.target.value, 10) }));
  };

  const handleDimensionChange = (e: SelectChangeEvent<string>) => {
    setSettings(prev => ({ ...prev, video_width: parseInt(e.target.value, 10) }));
  };

  const handleAspectRatioChange = (e: SelectChangeEvent<string>) => {
    setSettings(prev => ({ ...prev, aspect_ratio: e.target.value }));
  };

  const handleFormatChange = (e: SelectChangeEvent<string>) => {
    setSettings(prev => ({ ...prev, video_format: e.target.value }));
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => {
      const raw = e.target.value;
      if (raw === "" || raw === null) {
        return { ...prev, video_time: 0 };
      }
  
      const value = parseInt(raw, 10);
      return { ...prev, video_time: isNaN(value) ? 0 : value };
    });
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({ ...prev, background_color: e.target.value }));
  };

  const handleTransparentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({ ...prev, transparent: e.target.checked }));
  };

  const handleTimeModeChange = (e: SelectChangeEvent<string>) => {
    const newMode = e.target.value as 'total' | 'pixels';
    const current = settings.video_time;
    const contentHeight = calculateVideoHeight(settings);

    if (current <= 0) {
      setTimeMode(newMode);
      return;
    }

    const converted =
      newMode === 'pixels'
        ? Math.round((contentHeight / current) * 10) / 10
        : Math.round((contentHeight / current) * 10) / 10;

    setSettings(prev => ({ ...prev, video_time: converted }));
    setTimeMode(newMode);
  };

  const toggleCollapsed = () => setIsCollapsed(prev => !prev);

  return (
    <div className={`video-settings ${isCollapsed ? 'collapsed' : 'expanded'}`}>
      <div className="settings-header" onClick={toggleCollapsed}>
        <span className="settings-header-title">{t`Video Settings`}</span>
        <button
          type="button"
          className="settings-header-chevron"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={e => { e.stopPropagation(); toggleCollapsed(); }}
        >
          <KeyboardArrowUpIcon className={`chevron-icon ${isCollapsed ? '' : 'expanded'}`} />
        </button>
      </div>

      {!isCollapsed && (
        <div className="video-settings-group">
          {/* Background Color */}
          <div className="setting-item">
            <label>{t`Background Color`}</label>
            <div className="input-wrapper">
              {settings.transparent ? (
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    border: '2px solid #ddd',
                    borderRadius: '4px',
                    backgroundImage: `
                      linear-gradient(45deg, #ccc 25%, transparent 25%),
                      linear-gradient(-45deg, #ccc 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #ccc 75%),
                      linear-gradient(-45deg, transparent 75%, #ccc 75%)
                    `,
                    backgroundSize: '4px 4px',
                    backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    userSelect: 'none',
                  }}
                  title="Transparent background"
                >
                  T
                </div>
              ) : (
                <input type="color" value={settings.background_color} onChange={handleColorChange} />
              )}
              <label onClick={e => e.preventDefault()}>
                {t`Transparent`}:
                <input type="checkbox" checked={settings.transparent} onChange={handleTransparentChange} />
              </label>
            </div>
          </div>

          {/* FPS */}
          <div className="form-group" style={{ width: '140px' }}>
            <label>{t`FPS`}</label>
            <div className="input-wrapper">
              <Select
                value={settings.fps_num.toString()}
                onChange={handleFpsChange}
                style={{ width: '92px', height: '34px', marginLeft: '105px', fontSize: '14px' }}
                sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              >
                <MenuItem value="24">24</MenuItem>
                <MenuItem value="30">30</MenuItem>
                <MenuItem value="60">60</MenuItem>
                <MenuItem value="90">90</MenuItem>
                <MenuItem value="120">120</MenuItem>
              </Select>
            </div>
          </div>

          {/* Resolution */}
          <div className="form-group" style={{ width: '320px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {t`Resolution`}
              <div className="tooltip-wrapper">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text" style={{ paddingBottom: '1px' }}>
                  {t`This setting determines the size of the video. Adjust font size accordingly.`}
                </span>
              </div>
            </label>
            <div className="input-wrapper">
              <Select
                value={settings.video_width.toString()}
                onChange={handleDimensionChange}
                style={{ width: '200px', height: '34px', marginLeft: '33px', fontSize: '14px' }}
                sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              >
                <MenuItem value="1280">HD (720p)</MenuItem>
                <MenuItem value="1920">Full HD (1080p)</MenuItem>
                <MenuItem value="2560">QHD (1440p)</MenuItem>
                <MenuItem value="3840">4K UHD (2160p)</MenuItem>
              </Select>
            </div>
          </div>

          {/* Aspect Ratio */}
          <div className="form-group" style={{ width: '320px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {t`Aspect Ratio`}
              <div className="tooltip-wrapper" style={{ display: 'flex', alignItems: 'center', height: 'fit-content' }}>
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text-ratio">{t`This setting greatly affects the video size.`}</span>
              </div>
            </label>
            <div className="input-wrapper">
              <Select
                value={settings.aspect_ratio}
                onChange={handleAspectRatioChange}
                style={{ width: '200px', height: '34px', marginLeft: '33px', fontSize: '14px' }}
                sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              >
                <MenuItem value="16:9">16:9 (Widescreen)</MenuItem>
                <MenuItem value="4:3">4:3 (Standard)</MenuItem>
                <MenuItem value="1:1">1:1 (Square)</MenuItem>
                <MenuItem value="9:16">9:16 (Vertical)</MenuItem>
                <MenuItem value="21:9">21:9 (Cinematic)</MenuItem>
              </Select>
            </div>
          </div>

          {/* Format */}
          <div className="form-group" style={{ width: '220px' }}>
            <label>{t`Format`}</label>
            <div className="input-wrapper">
              <Select
                value={settings.video_format}
                onChange={handleFormatChange}
                style={{ width: '200px', height: '34px', marginLeft: '75px', fontSize: '14px' }}
                sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              >
                <MenuItem value="H264">H264</MenuItem>
                <MenuItem value="H265">H265</MenuItem>
                <MenuItem value="ProRes_422_Proxy">ProRes 422 Proxy</MenuItem>
                <MenuItem value="ProRes_422_LT">ProRes 422 LT</MenuItem>
                <MenuItem value="ProRes_422">ProRes 422</MenuItem>
                <MenuItem value="ProRes_422_HQ">ProRes 422 HQ</MenuItem>
                <MenuItem value="ProRes_4444">ProRes 4444</MenuItem>
                <MenuItem value="ProRes_4444_XQ">ProRes 4444 XQ</MenuItem>
              </Select>
            </div>
          </div>

          {/* Video Time */}
          <div className="setting-item">
            <label style={{ fontWeight: 500, marginLeft: '-3px' }}>
              {timeMode === 'total' ? t`Video Time (seconds)` : t`Pixels per second`}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <input
                id="time-setting"
                type="number"
                value={videoTimeInput}
                onChange={(e) => setVideoTimeInput(e.target.value)}
                onBlur={() => {
                  const raw = videoTimeInput.trim();
                  const num = parseInt(raw, 10);

                  setSettings(prev => ({
                    ...prev,
                    video_time: isNaN(num) ? 0 : num
                  }));

                  setVideoTimeInput(isNaN(num) ? "" : num.toString());
                }}
                className={videoTimeInput === "" ? "input-empty" : ""}
                style={{ fontSize: "16px" }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Select
                  value={timeMode}
                  onChange={handleTimeModeChange}
                  style={{ width: '110px', height: '34px', fontSize: '14px' }}
                  sx={{
                    '.MuiSelect-select': { textAlign: 'left', paddingLeft: '8px' },
                    '.MuiSelect-icon': { color: '#007CB2' }
                  }}
                  MenuProps={{ PaperProps: { style: { fontSize: 13 } } }}
                >
                  <MenuItem value="total">{t`Total Time`}</MenuItem>
                  <MenuItem value="pixels">{t`Pixels/sec`}</MenuItem>
                </Select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoSettingsComponent;
